import { z } from 'zod';

// Product accounting mapping, verified against P2 /api/status. Never an FX rate.
export const QUOTA_PER_CNY = 500000n;
const MICROS_PER_YUAN = 1000000n;
const MICROS_PER_QUOTA = MICROS_PER_YUAN / QUOTA_PER_CNY;
const MAX_QUOTA = 9223372036854775807n;
const decimal = /^(0|[1-9]\d{0,31})(?:\.\d{1,6})?$/;
export function cnyMicros(amount: string): bigint {
  if (typeof amount !== 'string' || !decimal.test(amount)) throw Error('INVALID_CNY_AMOUNT');
  const [whole, fraction = ''] = amount.split('.');
  return BigInt(whole!) * MICROS_PER_YUAN + BigInt(fraction.padEnd(6, '0'));
}
function fromMicros(value: bigint): string {
  const whole = value / MICROS_PER_YUAN;
  const fraction = (value % MICROS_PER_YUAN).toString().padStart(6, '0').replace(/0+$/, '');
  return whole.toString() + (fraction ? '.' + fraction : '');
}
export function quotaToCny(raw: unknown): string {
  if (typeof raw === 'number' && (!Number.isSafeInteger(raw) || raw < 0)) throw Error('INVALID_RAW_QUOTA');
  if (!['string', 'number', 'bigint'].includes(typeof raw) || !/^(0|[1-9]\d{0,18})$/.test(String(raw))) throw Error('INVALID_RAW_QUOTA');
  const quota = BigInt(String(raw));
  if (quota > MAX_QUOTA) throw Error('INVALID_RAW_QUOTA');
  return fromMicros(quota * MICROS_PER_QUOTA);
}
export function cnyToQuota(amount: string): bigint {
  const micros = cnyMicros(amount);
  if (micros % MICROS_PER_QUOTA !== 0n || micros / MICROS_PER_QUOTA > MAX_QUOTA) throw Error('CNY_AMOUNT_NOT_REPRESENTABLE');
  return micros / MICROS_PER_QUOTA;
}
export function formatCny(amount: string | null | undefined): string {
  if (amount == null) return '待核对';
  const [whole, fraction = ''] = fromMicros(cnyMicros(amount)).split('.');
  return '¥' + whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + fraction.padEnd(2, '0');
}
export const CnyAmount = z.string().regex(decimal).describe('人民币元十进制字符串，最多6位小数；禁止浮点、指数、符号、空格及隐式四舍五入。');
export const CnyMoney = z.object({ currency: z.literal('CNY'), unit: z.literal('yuan'), amount: CnyAmount }).strict();
// Future quotation/reservation/settlement contract only; no business endpoints added.
export const TaskAmounts = z.object({ currency: z.literal('CNY'), unit: z.literal('yuan'), quoted: CnyAmount.nullable(), reserved: CnyAmount.nullable(), charged: CnyAmount.nullable(), refunded: CnyAmount.nullable(), net: CnyAmount.nullable() }).strict().superRefine((a,ctx) => {
  if ([a.quoted,a.reserved,a.charged,a.refunded,a.net].some(value => value !== null && !CnyAmount.safeParse(value).success)) return;
  if (a.charged !== null && a.refunded !== null && (cnyMicros(a.refunded) > cnyMicros(a.charged) || a.net !== null && cnyMicros(a.net) !== cnyMicros(a.charged) - cnyMicros(a.refunded))) ctx.addIssue({ code:'custom', message:'AMOUNT_RELATION_INVALID' });
});
export const AuthorizationAmount = CnyAmount.refine(value => {
  try { const quota = cnyToQuota(value); return quota > 0n && quota <= BigInt(Number.MAX_SAFE_INTEGER); } catch { return false; }
}, '授权金额必须为正数、可精确映射到内部最小单位且不超安全上限。');
